export default {
  async mounted() {
    // Color scheme from figmaColors.ts
    this.colors = {
      green: {
        100: '#EBF9EC',
        200: '#D0F5D4',
        300: '#9AF0A1',
        500: '#11B424',
        600: '#05753F',
        700: '#025235'
      },
      gray: {
        50: '#F9FAFA',
        100: '#F0F4F5',
        200: '#DFE4E7',
        300: '#C6CED2',
        400: '#A7B0BA',
        500: '#69717A',
        600: '#4A525B',
        700: '#3A3F46'
      }
    }

    // Initialize transition defaults
    this.transitionDuration = 200 // Faster transitions
    this.transitionEase = d3.easePolyOut.exponent(3)

    try {
      // Wait for WASM to be available
      if (typeof window["@hpcc-js/wasm"] === 'undefined') {
        console.error('GraphViz WASM library not loaded')
        return
      }

      // Initialize GraphViz with explicit worker configuration
      this.graphviz = d3
        .select(this.el)
        .graphviz({
          useWorker: false,  // Disable web workers to avoid WASM URL issues
          useSharedWorker: false
        })
        .zoom(true)
        .fit(true)
        .width(this.el.clientWidth)
        .height(this.el.clientHeight)
        .on('end', () => {
          // Wait for next tick to ensure DOM is ready
          setTimeout(() => this.addNodeInteractions(), 0)
        })

      this.renderGraph()

      this.observer = new ResizeObserver(() => {
        if (this.graphviz) {
          this.graphviz
            .width(this.el.clientWidth)
            .height(this.el.clientHeight)
          this.renderGraph()
        }
      })
      this.observer.observe(this.el)

    } catch (error) {
      console.error('Error initializing GraphViz:', error)
    }
  },

  updated() {
    this.renderGraph()
  },

  destroyed() {
    if (this.observer) {
      this.observer.disconnect()
    }
    this.graphviz = null
  },

  renderGraph() {
    const dot = this.el.dataset.dot
    if (!dot || !this.graphviz) return

    try {
      this.graphviz.renderDot(dot)
    } catch (error) {
      console.error('Error rendering graph:', error)
    }
  },

  addNodeInteractions() {
    try {
      // Handle nodes
      const nodes = d3.selectAll('.node')
      if (!nodes.empty()) {
        nodes
          .style('cursor', 'pointer')
          .each((d, i, nodes) => {
            const node = d3.select(nodes[i])
            const shape = node.select('polygon, rect, ellipse')
            
            if (!shape.empty()) {
              // Check if filtered by looking for dashed style in DOT
              const style = shape.attr('style')
              const isFiltered = style ? style.includes('dashed') : false

              // Store original styles for hover reset
              const originalFill = shape.style('fill')
              const originalStroke = shape.style('stroke')
              node.datum({ originalFill, originalStroke, isFiltered })
            }
          })
          .on('mouseover', (event) => {
            const node = d3.select(event.currentTarget)
            const shape = node.select('polygon, rect, ellipse')
            const data = node.datum()
            
            if (!shape.empty() && data) {
              if (data.isFiltered) {
                // Hover effect for filtered nodes - use gray colors with dotted border
                shape
                  .transition()
                  .duration(this.transitionDuration)
                  .ease(this.transitionEase)
                  .style('fill', this.colors.gray[200]) // Slightly darker gray on hover
                  .style('stroke', this.colors.gray[500]) // Darker gray border on hover
                  .style('stroke-width', '1.5')
                  .style('stroke-dasharray', '2,2') // Dotted style
              } else {
                // Hover effect for active nodes - use vibrant green
                shape
                  .transition()
                  .duration(this.transitionDuration)
                  .ease(this.transitionEase)
                  .style('fill', this.colors.green[200]) // Brighter green fill
                  .style('stroke', this.colors.green[500]) // Vibrant green border
                  .style('stroke-width', '1.5')
              }
            }
          })
          .on('mouseout', (event) => {
            const node = d3.select(event.currentTarget)
            const shape = node.select('polygon, rect, ellipse')
            const data = node.datum()
            
            if (!shape.empty() && data) {
              shape
                .transition()
                .duration(this.transitionDuration)
                .ease(this.transitionEase)
                .style('fill', data.originalFill)
                .style('stroke', data.originalStroke)
                .style('stroke-width', '1')
                .style('stroke-dasharray', data.isFiltered ? '2,2' : null) // Maintain dotted style for filtered nodes
            }
          })
          .on('click', (event) => {
            const titleEl = event.currentTarget.querySelector('title')
            if (titleEl && titleEl.textContent) {
              this.pushEventTo('#graph-container', 'node_clicked', {
                id: titleEl.textContent
              })
            }
          })
      }

      // Handle edges
      const edges = d3.selectAll('.edge')
      if (!edges.empty()) {
        edges.each((d, i, edges) => {
          const edge = d3.select(edges[i])
          const path = edge.select('path')
          
          if (!path.empty()) {
            const style = path.attr('style')
            const isFiltered = style ? style.includes('dashed') : false

            // Store original styles
            const originalStroke = path.style('stroke')
            const originalWidth = path.style('stroke-width')
            edge.datum({ originalStroke, originalWidth, isFiltered })
          }
        })
        .on('mouseover', (event) => {
          const edge = d3.select(event.currentTarget)
          const path = edge.select('path')
          const data = edge.datum()
          
          if (!path.empty() && data) {
            if (data.isFiltered) {
              // Hover effect for filtered edges - use gray with dotted style
              path
                .transition()
                .duration(this.transitionDuration)
                .ease(this.transitionEase)
                .style('stroke', this.colors.gray[500]) // Darker gray on hover
                .style('stroke-width', '1.5')
                .style('stroke-dasharray', '2,2') // Dotted style
            } else {
              // Hover effect for active edges - use vibrant green
              path
                .transition()
                .duration(this.transitionDuration)
                .ease(this.transitionEase)
                .style('stroke', this.colors.green[500]) // Vibrant green
                .style('stroke-width', '1.5')
            }
          }
        })
        .on('mouseout', (event) => {
          const edge = d3.select(event.currentTarget)
          const path = edge.select('path')
          const data = edge.datum()
          
          if (!path.empty() && data) {
            path
              .transition()
              .duration(this.transitionDuration)
              .ease(this.transitionEase)
              .style('stroke', data.originalStroke)
              .style('stroke-width', '1')
              .style('stroke-dasharray', data.isFiltered ? '2,2' : null) // Maintain dotted style for filtered edges
          }
        })
      }
    } catch (error) {
      console.error('Error adding interactions:', error)
    }
  }
}
